/**
 * Logger utility to replace console statements with environment-based configuration
 * Supports different log levels and can be configured to disable logging in production
 */

// Log levels with priorities
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3,
  TRACE: 4
};

// Detect environment based on VITE_APP_BASE_URL
const detectEnvironment = () => {
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  if (!baseUrl || baseUrl.includes('localhost') ) {
    return 'development';
  } else if (baseUrl.includes('staging')) {
    return 'staging';
  } else if (baseUrl.includes('prompthire.org')) {
    return 'production';
  }
  // Fallback: use Vite's PROD flag
  return import.meta.env.PROD ? 'production' : 'development';
};

// Environment-specific configurations
const ENVIRONMENT_CONFIGS = {
  development: {
    level: LOG_LEVELS.DEBUG,
    enableInProduction: true,
    enableTimestamps: true,
    enableGrouping: true
  },
  staging: {
    level: LOG_LEVELS.INFO,
    enableInProduction: true,
    enableTimestamps: true,
    enableGrouping: true
  },
  production: {
    level: LOG_LEVELS.ERROR,
    enableInProduction: false,
    enableTimestamps: false,
    enableGrouping: false
  }
};

// Get current environment and config
const currentEnvironment = detectEnvironment();
const environmentConfig = ENVIRONMENT_CONFIGS[currentEnvironment] || ENVIRONMENT_CONFIGS.development;

// Configuration
const LOG_CONFIG = {
  // Set log level based on environment
  level: environmentConfig.level,
  
  // Enable/disable logging in production
  enableInProduction: environmentConfig.enableInProduction,
  
  // Enable timestamps
  enableTimestamps: environmentConfig.enableTimestamps,
  
  // Enable console grouping for better organization
  enableGrouping: environmentConfig.enableGrouping,
  
  // Current environment
  environment: currentEnvironment,
  
  // Custom prefixes for different log levels
  prefixes: {
    [LOG_LEVELS.ERROR]: '❌ ERROR',
    [LOG_LEVELS.WARN]: '⚠️  WARN',
    [LOG_LEVELS.INFO]: 'ℹ️  INFO',
    [LOG_LEVELS.DEBUG]: '🐛 DEBUG',
    [LOG_LEVELS.TRACE]: '🔍 TRACE'
  }
};

/**
 * Check if logging should be enabled for the given log level
 */
const shouldLog = (level) => {
  // In production environment, check if logging is enabled
  if (LOG_CONFIG.environment === 'production' && !LOG_CONFIG.enableInProduction) {
    // Only allow ERROR logs in production by default
    return level <= LOG_LEVELS.ERROR;
  }
  
  // For all other environments, check against configured log level
  return level <= LOG_CONFIG.level;
};

/**
 * Format log message with timestamp and prefix
 */
const formatMessage = (level, args) => {
  const parts = [];
  
  // Add timestamp if enabled
  if (LOG_CONFIG.enableTimestamps) {
    const timestamp = new Date().toLocaleTimeString();
    parts.push(`[${timestamp}]`);
  }
  
  // Add level prefix
  if (LOG_CONFIG.prefixes[level]) {
    parts.push(LOG_CONFIG.prefixes[level]);
  }
  
  return parts.length > 0 ? [parts.join(' '), ...args] : args;
};

/**
 * Logger class with different log levels
 */
class Logger {
  /**
   * Log error messages (always shown in production)
   */
  error(...args) {
    if (shouldLog(LOG_LEVELS.ERROR)) {
      const formatted = formatMessage(LOG_LEVELS.ERROR, args);
      console.error(...formatted);
      
      // In production/staging, you might want to send errors to an external service
      if (LOG_CONFIG.environment === 'production' || LOG_CONFIG.environment === 'staging') {
        this.reportError(args);
      }
    }
  }

  /**
   * Log warning messages
   */
  warn(...args) {
    if (shouldLog(LOG_LEVELS.WARN)) {
      const formatted = formatMessage(LOG_LEVELS.WARN, args);
      console.warn(...formatted);
    }
  }

  /**
   * Log info messages
   */
  info(...args) {
    if (shouldLog(LOG_LEVELS.INFO)) {
      const formatted = formatMessage(LOG_LEVELS.INFO, args);
      console.info(...formatted);
    }
  }

  /**
   * Log debug messages (development only by default)
   */
  debug(...args) {
    if (shouldLog(LOG_LEVELS.DEBUG)) {
      const formatted = formatMessage(LOG_LEVELS.DEBUG, args);
      console.log(...formatted);
    }
  }

  /**
   * Log trace messages (detailed debugging)
   */
  trace(...args) {
    if (shouldLog(LOG_LEVELS.TRACE)) {
      const formatted = formatMessage(LOG_LEVELS.TRACE, args);
      console.trace(...formatted);
    }
  }

  /**
   * Log function for general use (maps to debug level)
   */
  log(...args) {
    this.debug(...args);
  }

  /**
   * Group related log messages
   */
  group(label, collapsed = false) {
    if (shouldLog(LOG_LEVELS.DEBUG)) {
      if (collapsed) {
        console.groupCollapsed(label);
      } else {
        console.group(label);
      }
    }
  }

  /**
   * End log group
   */
  groupEnd() {
    if (shouldLog(LOG_LEVELS.DEBUG)) {
      console.groupEnd();
    }
  }

  /**
   * Log table data
   */
  table(data) {
    if (shouldLog(LOG_LEVELS.DEBUG)) {
      console.table(data);
    }
  }

  /**
   * Log timing information
   */
  time(label) {
    if (shouldLog(LOG_LEVELS.DEBUG)) {
      console.time(label);
    }
  }

  timeEnd(label) {
    if (shouldLog(LOG_LEVELS.DEBUG)) {
      console.timeEnd(label);
    }
  }

  /**
   * Assert condition and log error if false
   */
  assert(condition, ...args) {
    if (shouldLog(LOG_LEVELS.ERROR) && !condition) {
      this.error('Assertion failed:', ...args);
    }
  }

  /**
   * Report errors to external service (customize as needed)
   */
  reportError(errorData) {
    // Add your error reporting logic here
    // For example, send to Sentry, LogRocket, or your own error tracking service
    try {
      // Example: Send to your error tracking service
      // errorTrackingService.captureException(errorData);
    } catch (e) {
      // Fallback to console if error reporting fails
      console.error('Failed to report error:', e);
    }
  }

  /**
   * Configure logger settings
   */
  configure(config) {
    Object.assign(LOG_CONFIG, config);
  }

  /**
   * Get current configuration
   */
  getConfig() {
    return { ...LOG_CONFIG };
  }

  /**
   * Get current environment
   */
  getEnvironment() {
    return LOG_CONFIG.environment;
  }

  /**
   * Check if environment is production
   */
  isProduction() {
    return LOG_CONFIG.environment === 'production';
  }

  /**
   * Check if environment is staging
   */
  isStaging() {
    return LOG_CONFIG.environment === 'staging';
  }

  /**
   * Check if environment is development
   */
  isDevelopment() {
    return LOG_CONFIG.environment === 'development';
  }
}

// Create singleton logger instance
const logger = new Logger();

// Export both the logger instance and the class for advanced usage
export default logger;
export { Logger, LOG_LEVELS };

// For backwards compatibility, also export as named exports
export const { 
  error, 
  warn, 
  info, 
  debug, 
  trace, 
  log, 
  group, 
  groupEnd, 
  table, 
  time, 
  timeEnd, 
  assert, 
  getEnvironment, 
  isProduction, 
  isStaging, 
  isDevelopment,
  getConfig 
} = logger; 